import { Fragment, useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { twMerge } from "tailwind-merge";

export type CallDetailRow = {
  label: string;
  value: string;
};

type CallDetailsPopoverProps = {
  /** Pre-resolved rows (labels are shown in small caps via CSS). */
  rows: CallDetailRow[];
  title?: string;
  className?: string;
  /** When false, nothing is rendered. */
  visible?: boolean;
};

const shellTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const },
};

const crossfadeTransition = { duration: 0.12, ease: [0.22, 1, 0.36, 1] as const };

export function CallDetailsPopover({
  rows,
  title = "Call Details",
  className,
  visible = true,
}: CallDetailsPopoverProps) {
  const panelId = useId();
  /** Large screens show the details inline as a bar; small screens use the popover. */
  const isLargeScreen = useMediaQuery({ minWidth: 1024 });
  /** Which panel should be shown after the current exit animation finishes. */
  const [panel, setPanel] = useState<"collapsed" | "expanded">("collapsed");
  /**
   * Chrome (radius, width, shadow) for the shell. Stays on "pill" until the collapsed
   * button finishes exiting when opening, and stays on "card" until the expanded panel
   * finishes exiting when closing — so radius/size never fight the inner crossfade.
   */
  const [shellCard, setShellCard] = useState(false);
  const panelRef = useRef(panel);
  panelRef.current = panel;

  const open = useCallback(() => setPanel("expanded"), []);
  const close = useCallback(() => setPanel("collapsed"), []);

  useEffect(() => {
    if (!visible || rows.length === 0) {
      setPanel("collapsed");
      setShellCard(false);
    }
  }, [visible, rows.length]);

  const onInnerExitComplete = useCallback(() => {
    setShellCard(panelRef.current === "expanded");
  }, []);

  if (!visible || rows.length === 0) {
    return null;
  }

  if (isLargeScreen) {
    return (
      <div className={twMerge("pointer-events-auto max-w-[calc(100%-5rem)]", className)}>
        <div
          role="region"
          aria-label={title}
          className="inline-flex items-stretch overflow-hidden rounded-2xl bg-[#FCFAF8] text-zinc-800 border border-black/[0.04] shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18)]"
        >
          {rows.map(({ label, value }, index) => (
            <Fragment key={label}>
              {index > 0 && <div className="w-px h-7 self-center bg-black/[0.08]" />}
              <div className="min-w-0 flex flex-col gap-1 px-5 py-3">
                <span className="text-xs font-semibold text-gray-701 uppercase break-words">
                  {label}
                </span>
                <span className="text-xs font-normal text-gray-701 break-words [overflow-wrap:anywhere] leading-snug">
                  {value}
                </span>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={twMerge("pointer-events-auto", className)}>
      <motion.div
        layout
        initial={false}
        transition={shellTransition}
        className={twMerge(
          "bg-[#FCFAF8] text-zinc-800 border border-black/[0.04]",
          shellCard
            ? "rounded-[26px] shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18)] w-[min(100vw-2rem,225px)]"
            : "rounded-full shadow-[0_2px_12px_-4px_rgba(0,0,0,0.12)]",
        )}
      >
        <AnimatePresence initial={false} mode="wait" onExitComplete={onInnerExitComplete}>
          {panel === "collapsed" ? (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={crossfadeTransition}
            >
              <button
                type="button"
                className="w-full px-6 py-3 text-sm font-semibold text-gray-701 text-center"
                aria-expanded={false}
                aria-controls={panelId}
                onClick={open}
              >
                {title}
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="expanded"
              id={panelId}
              role="region"
              aria-label={title}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={crossfadeTransition}
              className="min-w-0 px-[18px] relative py-4 bg-white/[70%] rounded-[25px] border border-white/10 shadow-[0_-2px_4px_0_rgba(255,255,255,0.20)_inset,_0_1px_3px_0_rgba(255,255,255,0.30)_inset]"
            >
              <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
                <h2 className="min-w-0 flex-1 text-sm font-semibold text-gray-701 leading-tight break-words pr-2">
                  {title}
                </h2>
                <button
                  type="button"
                  className="absolute top-4 right-3 hover:opacity-80 transition-opacity"
                  aria-label={`Close ${title}`}
                  onClick={close}
                >
                  <CloseIcon className="w-5 h-5 [&_path]:stroke-current" />
                </button>
              </div>
              <ul className="flex flex-col gap-4">
                {rows.map(({ label, value }) => (
                  <li key={label} className="flex flex-col gap-1 min-w-0">
                    <span className="text-xs font-semibold text-gray-701 uppercase break-words">
                      {label}
                    </span>
                    <span className="text-xs font-normal text-gray-701 break-words [overflow-wrap:anywhere] leading-snug">
                      {value}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
